import { fetchStream } from '../api/stream';
import { updateStats } from '../api/stats';
import { contentRepo } from '../db/contentRepo';
import { mediaRepo } from '../db/mediaRepo';
import { prefs } from '../db/prefs';
import { contentChanged, streamItemToContent } from '../types/content';
import { proxiedMediaUrl } from '../helpers/mediaUrl';
import type { StreamConfig, StreamItem } from '../types/stream';

export interface SyncProgress {
  index: number;
  total: number;
}

export interface SyncResult {
  error: string | null;
  // True when the playlist visibly changed and the player should reload.
  restart: boolean;
  config: StreamConfig | null;
}

let busy = false;

// Full content sync. Mirrors DownloadManager.start():
// increment sequence -> fetch stream -> download missing/changed media ->
// upsert rows -> delete stale rows + blobs -> push stats.
export async function runSync(onProgress?: (p: SyncProgress) => void): Promise<SyncResult> {
  if (busy) return { error: null, restart: false, config: null };
  busy = true;

  let inserts = 0;
  let changes = 0;
  let downloads = 0;
  let deletes = 0;

  try {
    const sequence = (await prefs.getSequence()) + 1;

    const stream = await fetchStream();
    const config = stream.config ?? null;
    if (config) await prefs.setConfig(config);

    const items = stream.data ?? [];

    for (let i = 0; i < items.length; i++) {
      onProgress?.({ index: i, total: items.length });
      const item = items[i];

      const downloaded = await downloadMedia(item);
      if (downloaded) downloads++;

      const row = streamItemToContent(item, sequence);
      const existing = await contentRepo.getByUuid(item.uuid);
      if (!existing) {
        inserts++;
      } else if (contentChanged(existing, row)) {
        changes++;
      }
      await contentRepo.put(row);
    }

    // Remove stale content (rows from a previous sequence) and their blobs.
    const stale = await contentRepo.getOlderThan(sequence);
    for (const row of stale) {
      if (row.fileName) await mediaRepo.delete(row.fileName);
      await contentRepo.delete(row.uuid);
      deletes++;
    }

    await prefs.setSequence(sequence);

    const stats = downloads > 0 || deletes > 0;
    const restart = inserts > 0 || changes > 0 || stats;

    if (stats) {
      await updateStats();
    }

    return { error: null, restart, config };
  } catch (e) {
    return { error: (e as Error).message || 'Sync failed', restart: false, config: null };
  } finally {
    busy = false;
  }
}

async function downloadMedia(item: StreamItem): Promise<boolean> {
  const file = item.file;
  if (!file || !file.name || !file.url) return false;

  if (await mediaRepo.hasExact(file.name, file.size)) {
    return false;
  }

  const res = await fetch(proxiedMediaUrl(file.url));
  if (!res.ok) {
    throw new Error(`Download failed (${res.status}) for ${file.name}`);
  }
  const blob = await res.blob();
  await mediaRepo.put({ name: file.name, size: blob.size, blob });
  return true;
}
